/**
 * Carries THIS tab's account into a NEW tab opened from an in-app link.
 */

import { accountHandoffUrl } from "@/lib/canonical-link";
import { readTabAccountId } from "@/lib/tab-account";

let installed = false;

/** The anchor a click landed on, or `null` when the click was not on one. */
function anchorFromEvent(event: Event): HTMLAnchorElement | null {
  const path = event.composedPath();
  for (const node of path) {
    if (node instanceof HTMLAnchorElement) {
      return node;
    }
  }
  return null;
}

/** True for a plain, same-origin, in-app anchor we may re-open ourselves. */
function isInAppAnchor(anchor: HTMLAnchorElement): boolean {
  if (anchor.target !== "" || anchor.hasAttribute("download")) {
    return false;
  }
  const href = anchor.getAttribute("href");
  if (href === null || href === "" || href.startsWith("#")) {
    return false;
  }
  try {
    return new URL(anchor.href).origin === globalThis.location.origin;
  } catch {
    return false;
  }
}

/**
 * True when this click means "open in a new TAB": ctrl (Windows/Linux), cmd
 * (macOS) or the middle button. Shift means "new window" and is left alone.
 */
function opensInNewTab(event: MouseEvent): boolean {
  if (event.shiftKey) {
    return false;
  }
  return event.ctrlKey || event.metaKey || event.button === 1;
}

function handleClick(event: MouseEvent): void {
  if (event.defaultPrevented || !opensInNewTab(event)) {
    return;
  }
  const anchor = anchorFromEvent(event);
  if (!anchor || !isInAppAnchor(anchor)) {
    return;
  }
  const userId = readTabAccountId();
  if (userId === null) {
    // Nothing to carry — let the browser do its normal thing.
    return;
  }
  event.preventDefault();
  globalThis.open(accountHandoffUrl(anchor.href, userId), "_blank");
}

/**
 * Installs the delegated new-tab handoff. Lifetime singleton, idempotent, and a
 * no-op without a DOM.
 */
export function installNewTabAccountHandoff(): void {
  if (installed || typeof globalThis.addEventListener !== "function") {
    return;
  }
  installed = true;
  globalThis.addEventListener("click", handleClick, { capture: true });
  globalThis.addEventListener("auxclick", handleClick, { capture: true });
}
